import type { Parent, PhrasingContent, Root, Text } from 'mdast'
import { visit } from 'unist-util-visit'

interface Delimiter {
  child: number
  offset: number
  canOpen: boolean
  canClose: boolean
}

interface DelimiterPair {
  open: Delimiter
  close: Delimiter
}

const makeMark = (children: PhrasingContent[]): PhrasingContent =>
  ({
    // mdast has no highlight node type; `hName` instructs mdast-util-to-hast
    // to emit a real <mark> element.
    type: 'mark',
    data: { hName: 'mark' },
    children,
  }) as unknown as PhrasingContent

const isText = (node: PhrasingContent | undefined): node is Text =>
  node?.type === 'text'

// Highlight content mirrors the editor's single-line `==...==` syntax: a pair
// is rejected when any literal text between the delimiters contains a stray
// `=` or a newline, or when a hard break sits between them. Nested phrasing
// nodes (strong, links, images, inline code) are opaque content: their values
// and URLs are never inspected or rewritten.
const textSegmentIsClean = (segment: string): boolean =>
  !segment.includes('=') && !segment.includes('\n')

// Flanking rules, mirroring how `==text==` behaves in editors that support
// it (Pandoc mark syntax): a delimiter can OPEN a highlight only when
// followed by non-whitespace, and CLOSE one only when preceded by
// non-whitespace. This keeps prose like `a == b == c` and base64 padding
// (`QUJD== ... RUZH==`) literal while `==text==` and `value==inline==`
// still pair. Non-text siblings (strong, links, images) count as content;
// hard breaks count as whitespace.
const charAdjacent = (
  children: PhrasingContent[],
  child: number,
  step: -1 | 1,
): string => {
  const sibling = children[child + step]
  if (sibling === undefined) return ' '
  if (sibling.type === 'break') return ' '
  if (!isText(sibling)) return '\uffff'
  const value = sibling.value
  return (step === -1 ? value[value.length - 1] : value[0]) ?? ' '
}

const collectDelimiters = (children: PhrasingContent[]): Delimiter[] => {
  const delimiters: Delimiter[] = []
  children.forEach((child, index) => {
    if (!isText(child)) return
    let offset = child.value.indexOf('==')
    while (offset !== -1) {
      const before =
        offset > 0 ? child.value[offset - 1] : charAdjacent(children, index, -1)
      const after =
        offset + 2 < child.value.length
          ? child.value[offset + 2]
          : charAdjacent(children, index, 1)
      delimiters.push({
        child: index,
        offset,
        canOpen: !/\s/.test(after),
        canClose: !/\s/.test(before),
      })
      offset = child.value.indexOf('==', offset + 2)
    }
  })
  return delimiters
}

const isValidPair = (
  children: PhrasingContent[],
  open: Delimiter,
  close: Delimiter,
): boolean => {
  const openNode = children[open.child]
  const closeNode = children[close.child]
  if (!isText(openNode) || !isText(closeNode)) return false

  if (open.child === close.child) {
    const content = openNode.value.slice(open.offset + 2, close.offset)
    return content.length > 0 && textSegmentIsClean(content)
  }

  const openTail = openNode.value.slice(open.offset + 2)
  const closeHead = closeNode.value.slice(0, close.offset)
  if (!textSegmentIsClean(openTail) || !textSegmentIsClean(closeHead)) {
    return false
  }
  for (let index = open.child + 1; index < close.child; index += 1) {
    const between = children[index]
    if (between.type === 'break') return false
    if (isText(between) && !textSegmentIsClean(between.value)) return false
  }
  const hasContent =
    openTail.length > 0 || closeHead.length > 0 || close.child - open.child > 1
  return hasContent
}

// Greedy left-to-right pairing over flanking-eligible delimiters: a failed
// candidate abandons its opener and the closing delimiter becomes the next
// candidate opener.
const pairDelimiters = (
  children: PhrasingContent[],
  delimiters: Delimiter[],
): DelimiterPair[] => {
  const pairs: DelimiterPair[] = []
  let index = 0
  while (index < delimiters.length - 1) {
    const open = delimiters[index]
    if (!open.canOpen) {
      index += 1
      continue
    }
    const close = delimiters[index + 1]
    if (close.canClose && isValidPair(children, open, close)) {
      pairs.push({ open, close })
      index += 2
    } else {
      index += 1
    }
  }
  return pairs
}

const rebuildChildren = (
  children: PhrasingContent[],
  pairs: DelimiterPair[],
): PhrasingContent[] => {
  const out: PhrasingContent[] = []
  let childIndex = 0
  // Consumed prefix of the text node currently at `childIndex`.
  let offset = 0

  const pushSlice = (
    target: PhrasingContent[],
    node: Text,
    from: number,
    to: number,
  ) => {
    if (to > from) {
      target.push({ type: 'text', value: node.value.slice(from, to) })
    }
  }

  const flushUntil = (stop: number) => {
    while (childIndex < stop) {
      const child = children[childIndex]
      if (isText(child)) {
        pushSlice(out, child, offset, child.value.length)
      } else {
        out.push(child)
      }
      childIndex += 1
      offset = 0
    }
  }

  for (const { open, close } of pairs) {
    flushUntil(open.child)
    const openNode = children[open.child] as Text
    pushSlice(out, openNode, offset, open.offset)

    const markChildren: PhrasingContent[] = []
    if (open.child === close.child) {
      pushSlice(markChildren, openNode, open.offset + 2, close.offset)
    } else {
      pushSlice(markChildren, openNode, open.offset + 2, openNode.value.length)
      for (let index = open.child + 1; index < close.child; index += 1) {
        markChildren.push(children[index])
      }
      const closeNode = children[close.child] as Text
      pushSlice(markChildren, closeNode, 0, close.offset)
    }
    out.push(makeMark(markChildren))

    childIndex = close.child
    offset = close.offset + 2
  }

  flushUntil(children.length)
  return out
}

/**
 * remark plugin converting `==highlight==` spans into `<mark>` elements.
 *
 * Operating on the mdast (instead of preprocessing the source string) means
 * fenced code, inline code, and link/image destinations can never be
 * rewritten: those live in value nodes and URL properties, which this plugin
 * never touches. Delimiters are paired among the direct children of each
 * phrasing container, so a highlight may wrap nested inline markup
 * (`==__bold__==`, `==see [docs](url)==`) — the shape the editor's own
 * serializer emits for combined marks. Shared by the static file-preview
 * renderer and the TipTap `markdownToHtml` pipeline.
 */
export const remarkHighlightMarks = () => (tree: Root) => {
  visit(tree, (node) => {
    if (!('children' in node)) return
    const parent = node as Parent
    const children = parent.children as PhrasingContent[]
    const delimiters = collectDelimiters(children)
    if (delimiters.length < 2) return
    const pairs = pairDelimiters(children, delimiters)
    if (pairs.length === 0) return
    parent.children = rebuildChildren(children, pairs)
  })
}
